import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { commercialAutoPage, companyInfo } from '../mock';
import { CheckCircle2, Phone, MessageCircle, Truck } from 'lucide-react';

const CommercialAutoInsurance = () => {
  const { language } = useLanguage();
  const content = commercialAutoPage[language];

  const whatsappUrl = `https://wa.me/${companyInfo.phoneWhatsApp}?text=${encodeURIComponent(content.whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-700 to-orange-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <Truck className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-orange-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-orange-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Examples */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Coverage Examples' : 'Ejemplos de Cobertura'}
            </h2>
            <div className="grid gap-4">
              {content.coverageExamples.map((example, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Prepare */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.whatToPrepare.title}
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
              <ul className="space-y-3">
                {content.whatToPrepare.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-700 to-orange-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language === 'en' ? 'Get Your Commercial Auto Quote' : 'Obtenga Su Cotización de Auto Comercial'}
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              {language === 'en' 
                ? 'Protect your business vehicles today' 
                : 'Proteja sus vehículos comerciales hoy'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-orange-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-50 transition-all shadow-lg"
              >
                <MessageCircle className="w-6 h-6" />
                {language === 'en' ? 'Quote via WhatsApp' : 'Cotizar por WhatsApp'}
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-orange-800 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-900 transition-all shadow-lg"
              >
                <Phone className="w-6 h-6" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Partners */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Highway Certified Agency - Official Hero Image */}
          <div className="max-w-5xl mx-auto mb-8">
            <img 
              src="/images/trucking-hero.png" 
              alt="Aseguramos tu Truck y tu Mercancía - Highway Certified Agency - Palerm Insurance Agency" 
              className="w-full h-auto rounded-lg shadow-xl"
            />
          </div>

          <h3 className="text-2xl font-bold text-center mb-8 text-gray-900">
            {language === 'en' ? 'Our Insurance Partners' : 'Nuestros Socios Aseguradores'}
          </h3>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 items-center justify-items-center">
              <div className="flex items-center justify-center h-16">
                <img 
                  src="/images/carriers/progressive.svg" 
                  alt="Progressive" 
                  className="h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
              <div className="flex items-center justify-center h-16">
                <img 
                  src="/images/carriers/northland.svg" 
                  alt="Northland Insurance" 
                  className="h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
              <div className="flex items-center justify-center h-16 col-span-2 md:col-span-1">
                <img 
                  src="/images/carriers/geico.svg" 
                  alt="GEICO" 
                  className="h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
            <p className="text-center text-sm text-gray-500 mt-6">
              {language === 'en' 
                ? 'Partners shown for reference. Coverage availability varies by location and business type.' 
                : 'Socios mostrados como referencia. La disponibilidad varía según ubicación y tipo de negocio.'}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommercialAutoInsurance;